"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import {
  registerInterest,
  saveListing,
  sendListingMessage,
  unsaveListing,
} from "@/app/actions/listings";
import type { UserRole } from "@/lib/auth";
import { trackEvent } from "@/lib/analytics";
import {
  type CandidateListingState,
  type CandidateWindow,
} from "@/lib/marketplace/candidate-listing-state";
import { buildMatchExplanations } from "@/lib/marketplace/job-state";
import type {
  ListingDiscoverySource,
  ListingWithCompany,
} from "@/lib/marketplace/listing-types";
import { LOCATIONS_TREE, ROLES_TREE, findLabelById } from "@/lib/taxonomy";
import { evaluateEligibility } from "@/lib/marketplace/eligibility";
import { ListingCard } from "./ListingCard";

type GridDiscoverySource = Extract<
  ListingDiscoverySource,
  "jobs_marketplace" | "designated_employer_page"
>;

export interface ListingInteractionGridProps {
  listings: ListingWithCompany[];
  userRole: UserRole;
  candidateState: CandidateListingState;
  discoverySource: GridDiscoverySource;
  emptyState?: ReactNode;
  actionAdapter?: ListingInteractionActionAdapter;
  actionsDisabled?: boolean;
}

export interface ListingInteractionActionAdapter {
  registerInterest: (
    listingId: string,
    windowId: string,
    discoverySource: GridDiscoverySource,
  ) => Promise<{ error?: string }>;
  saveListing: (
    listingId: string,
    isSaved: boolean,
    discoverySource: GridDiscoverySource,
  ) => Promise<{ error?: string }>;
  sendMessage: (
    listingId: string,
    content: string,
    windowId: string,
    discoverySource: GridDiscoverySource,
  ) => Promise<{ error?: string; conversationId?: string }>;
}

type ListingAction = "interest" | "message";
type SuccessFocusTarget = {
  kind: "interest" | "conversation";
  listingId: string;
};

const MODAL_FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function modalFocusableElements(dialog: HTMLElement): HTMLElement[] {
  return Array.from(
    dialog.querySelectorAll<HTMLElement>(MODAL_FOCUSABLE_SELECTOR),
  ).filter(
    (element) =>
      element.getAttribute("aria-hidden") !== "true" &&
      !element.hasAttribute("disabled"),
  );
}

function AccessibleModal({
  labelledBy,
  onClose,
  initialFocusRef,
  returnFocusRef,
  maxWidthClass,
  children,
}: {
  labelledBy: string;
  onClose: () => void;
  initialFocusRef?: RefObject<HTMLElement | null>;
  returnFocusRef: RefObject<HTMLElement | null>;
  maxWidthClass: string;
  children: ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    const returnFocusTo = returnFocusRef.current;
    if (!dialog) return () => returnFocusTo?.focus();
    const initialFocus =
      initialFocusRef?.current ?? modalFocusableElements(dialog)[0] ?? dialog;
    initialFocus.focus();
    return () => returnFocusTo?.focus();
  }, [initialFocusRef, returnFocusRef]);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;

    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = modalFocusableElements(dialog);
    if (focusable.length === 0) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[4px]"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        className={`relative z-10 bg-white rounded-[16px] p-5 sm:p-7 w-full ${maxWidthClass} shadow-[0_24px_80px_rgba(0,0,0,0.15)]`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    </div>
  );
}

export type ListingActionIntent =
  | { kind: "join" }
  | { kind: "no_availability" }
  | {
      kind: "register_interest";
      listingId: string;
      windowId: string;
    }
  | {
      kind: "compose_message";
      listingId: string;
      windowId: string;
    }
  | {
      kind: "pick_window";
      action: ListingAction;
      listingId: string;
    };

export function resolveListingActionIntent(input: {
  action: ListingAction;
  listingId: string;
  userRole: UserRole;
  windows: readonly CandidateWindow[];
}): ListingActionIntent {
  if (input.userRole !== "candidate") return { kind: "join" };
  if (input.windows.length === 0) return { kind: "no_availability" };
  if (input.windows.length > 1) {
    return {
      kind: "pick_window",
      action: input.action,
      listingId: input.listingId,
    };
  }

  const windowId = input.windows[0].id;
  return input.action === "interest"
    ? { kind: "register_interest", listingId: input.listingId, windowId }
    : { kind: "compose_message", listingId: input.listingId, windowId };
}

export function runInterestAction(
  listingId: string,
  windowId: string,
  discoverySource: GridDiscoverySource,
) {
  return registerInterest(listingId, windowId, discoverySource);
}

export function runSaveAction(
  listingId: string,
  isSaved: boolean,
  discoverySource: GridDiscoverySource,
) {
  return isSaved
    ? unsaveListing(listingId, discoverySource)
    : saveListing(listingId, discoverySource);
}

export function runMessageAction(
  listingId: string,
  content: string,
  windowId: string,
  discoverySource: GridDiscoverySource,
) {
  return sendListingMessage(listingId, content, windowId, discoverySource);
}

const productionActionAdapter: ListingInteractionActionAdapter = {
  registerInterest: runInterestAction,
  saveListing: runSaveAction,
  sendMessage: runMessageAction,
};

export function navigateToListingDetail(
  listing: ListingWithCompany,
  discoverySource: GridDiscoverySource,
  push: (href: string) => void,
  navigate = true,
) {
  if (discoverySource === "designated_employer_page") {
    trackEvent("designated_job_detail_clicked", {
      company_id: listing.company_id,
      listing_id: listing.id,
      discovery_source: "designated_employer_page",
    });
  }
  if (navigate) push(`/jobs/${listing.id}`);
}

export function ListingInteractionGrid({
  listings,
  userRole,
  candidateState,
  discoverySource,
  emptyState,
  actionAdapter = productionActionAdapter,
  actionsDisabled = false,
}: ListingInteractionGridProps) {
  const router = useRouter();
  const returnFocusElementRef = useRef<HTMLElement | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const noAvailabilityCloseRef = useRef<HTMLButtonElement | null>(null);
  const interestSuccessRef = useRef<HTMLSpanElement | null>(null);
  const conversationSuccessRef = useRef<HTMLAnchorElement | null>(null);
  const [localInterests, setLocalInterests] = useState<Set<string>>(new Set());
  const [localConversations, setLocalConversations] = useState<
    Record<string, string>
  >({});
  const [localSavedListings, setLocalSavedListings] = useState<Set<string>>(
    new Set(),
  );
  const [localUnsavedListings, setLocalUnsavedListings] = useState<Set<string>>(
    new Set(),
  );
  const [activeListingId, setActiveListingId] = useState<string | null>(null);
  const [windowPickerAction, setWindowPickerAction] =
    useState<ListingAction>("interest");
  const [showWindowPicker, setShowWindowPicker] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showNoAvailability, setShowNoAvailability] = useState(false);
  const [selectedWindowId, setSelectedWindowId] = useState<string | null>(null);
  const [messageContent, setMessageContent] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [savedLoadingId, setSavedLoadingId] = useState<string | null>(null);
  const [interestError, setInterestError] = useState<string | null>(null);
  const [successFocusTarget, setSuccessFocusTarget] =
    useState<SuccessFocusTarget | null>(null);

  const interestedSet = new Set([
    ...candidateState.interestedListingIds,
    ...localInterests,
  ]);
  const savedSet = new Set([
    ...candidateState.savedListingIds,
    ...localSavedListings,
  ]);
  for (const listingId of localUnsavedListings) savedSet.delete(listingId);
  const conversationsByListing = {
    ...candidateState.conversationsByListing,
    ...localConversations,
  };

  useEffect(() => {
    const shouldLock =
      showWindowPicker || showMessageModal || showNoAvailability;
    document.body.style.overflow = shouldLock ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showMessageModal, showNoAvailability, showWindowPicker]);

  useEffect(() => {
    if (!successFocusTarget) return;
    const target = successFocusTarget.kind === "interest"
      ? interestSuccessRef.current
      : conversationSuccessRef.current;
    if (target?.isConnected) target.focus();
  }, [successFocusTarget]);

  async function registerCandidateInterest(listingId: string, windowId: string) {
    setActionLoading(true);
    setInterestError(null);
    const result = await actionAdapter.registerInterest(
      listingId,
      windowId,
      discoverySource,
    );
    if (!result.error) {
      setLocalInterests((current) => new Set(current).add(listingId));
      setSuccessFocusTarget({ kind: "interest", listingId });
    } else {
      setInterestError(result.error);
      window.setTimeout(() => setInterestError(null), 4_000);
    }
    setActionLoading(false);
    setShowWindowPicker(false);
  }

  function beginAction(
    action: ListingAction,
    listingId: string,
    returnFocusTo: HTMLElement,
  ) {
    if (actionsDisabled) return;
    returnFocusElementRef.current = returnFocusTo;
    setActiveListingId(listingId);
    const listing = listings.find((item) => item.id === listingId);
    if (userRole === "candidate" && listing && evaluateEligibility(listing.eligibility_requirements, candidateState.eligibilityProfile, listing.start_date).status !== "eligible") {
      router.push(`/jobs/${listingId}#job-requirements`);
      return;
    }
    const intent = resolveListingActionIntent({
      action,
      listingId,
      userRole,
      windows: candidateState.windows,
    });

    switch (intent.kind) {
      case "join":
        router.push(
          `/signup?intent=work&next=${encodeURIComponent(`/jobs/${listingId}`)}`,
        );
        break;
      case "no_availability":
        setShowNoAvailability(true);
        break;
      case "register_interest":
        void registerCandidateInterest(intent.listingId, intent.windowId);
        break;
      case "compose_message":
        setSelectedWindowId(intent.windowId);
        setMessageContent("");
        setShowMessageModal(true);
        break;
      case "pick_window":
        setWindowPickerAction(intent.action);
        setShowWindowPicker(true);
        break;
    }
  }

  async function handleSaveClick(listingId: string) {
    if (actionsDisabled) return;
    if (userRole !== "candidate") {
      router.push(
        `/signup?intent=work&next=${encodeURIComponent(`/jobs/${listingId}`)}`,
      );
      return;
    }

    const wasSaved = savedSet.has(listingId);
    setSavedLoadingId(listingId);
    if (wasSaved) {
      setLocalUnsavedListings((current) => new Set(current).add(listingId));
      setLocalSavedListings((current) => {
        const next = new Set(current);
        next.delete(listingId);
        return next;
      });
    } else {
      setLocalSavedListings((current) => new Set(current).add(listingId));
      setLocalUnsavedListings((current) => {
        const next = new Set(current);
        next.delete(listingId);
        return next;
      });
    }

    const result = await actionAdapter.saveListing(
      listingId,
      wasSaved,
      discoverySource,
    );
    if (result.error) {
      if (wasSaved) {
        setLocalUnsavedListings((current) => {
          const next = new Set(current);
          next.delete(listingId);
          return next;
        });
      } else {
        setLocalSavedListings((current) => {
          const next = new Set(current);
          next.delete(listingId);
          return next;
        });
      }
    }
    setSavedLoadingId(null);
  }

  function handleWindowSelected(windowId: string) {
    setShowWindowPicker(false);
    if (windowPickerAction === "interest" && activeListingId) {
      void registerCandidateInterest(activeListingId, windowId);
      return;
    }
    setSelectedWindowId(windowId);
    setMessageContent("");
    setShowMessageModal(true);
  }

  async function handleSendMessage() {
    if (actionsDisabled) return;
    if (!activeListingId || !selectedWindowId || !messageContent.trim()) return;
    if (!listings.some((listing) => listing.id === activeListingId)) return;
    setInterestError(null);
    setActionLoading(true);
    const result = await actionAdapter.sendMessage(
      activeListingId,
      messageContent.trim(),
      selectedWindowId,
      discoverySource,
    );
    if (!result.error && result.conversationId) {
      setLocalConversations((current) => ({
        ...current,
        [activeListingId]: result.conversationId as string,
      }));
      setLocalInterests((current) => new Set(current).add(activeListingId));
      setShowMessageModal(false);
      setMessageContent("");
      setSuccessFocusTarget({
        kind: "conversation",
        listingId: activeListingId,
      });
    } else if (result.error && result.conversationId) {
      setShowMessageModal(false);
      router.push(`/inbox/${result.conversationId}`);
    } else {
      setInterestError(result.error || "Your message could not be sent. Please try again.");
    }
    setActionLoading(false);
  }

  if (listings.length === 0) return <>{emptyState ?? null}</>;

  const activeListing = activeListingId
    ? listings.find((listing) => listing.id === activeListingId) ?? null
    : null;

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
        {listings.map((listing) => (
          <ListingCard
            eligibilityProfile={candidateState.eligibilityProfile}
            key={listing.id}
            listing={listing}
            userRole={userRole}
            isInterested={interestedSet.has(listing.id)}
            isSaved={savedSet.has(listing.id)}
            conversationId={conversationsByListing[listing.id]}
            onInterestClick={(event) =>
              beginAction("interest", listing.id, event.currentTarget)
            }
            onMessageClick={(event) =>
              beginAction("message", listing.id, event.currentTarget)
            }
            onSaveClick={() => void handleSaveClick(listing.id)}
            onListingDetailClick={(navigate) =>
              navigateToListingDetail(
                listing,
                discoverySource,
                router.push,
                navigate,
              )
            }
            isLoading={activeListingId === listing.id && actionLoading}
            isSaveLoading={savedLoadingId === listing.id}
            interestSuccessRef={
              successFocusTarget?.kind === "interest" &&
              successFocusTarget.listingId === listing.id
                ? interestSuccessRef
                : undefined
            }
            conversationSuccessRef={
              successFocusTarget?.kind === "conversation" &&
              successFocusTarget.listingId === listing.id
                ? conversationSuccessRef
                : undefined
            }
            actionsDisabled={actionsDisabled}
            matchReasons={
              userRole === "candidate"
                ? buildMatchExplanations({
                    listing: {
                      roles: listing.roles,
                      locations: listing.locations,
                      startDate: listing.start_date,
                      endDate: listing.end_date,
                    },
                    candidateWindows: candidateState.windows.map((window) => ({
                      positions: window.positions,
                      preferredLocations: window.preferred_locations,
                      availableFrom: window.available_from,
                      availableTo: window.available_to,
                    })),
                    candidateRightToWork: candidateState.rightToWork,
                    labelLookup: (id) =>
                      findLabelById(ROLES_TREE, id) ??
                      findLabelById(LOCATIONS_TREE, id) ??
                      id,
                  })
                : []
            }
          />
        ))}
      </div>

      {showWindowPicker && (
        <JobsWindowPickerModal
          windows={candidateState.windows}
          action={windowPickerAction}
          onSelect={handleWindowSelected}
          onClose={() => setShowWindowPicker(false)}
          returnFocusRef={returnFocusElementRef}
        />
      )}

      {showMessageModal && activeListing && selectedWindowId && (
        <AccessibleModal
          labelledBy="listing-message-title"
          onClose={() => setShowMessageModal(false)}
          initialFocusRef={messageInputRef}
          returnFocusRef={returnFocusElementRef}
          maxWidthClass="max-w-[520px]"
        >
          <button
            type="button"
            onClick={() => setShowMessageModal(false)}
            className="absolute top-4 right-4 min-w-[44px] min-h-[44px] rounded-full bg-warm-bg flex items-center justify-center text-text-muted hover:text-charcoal transition-colors border-none cursor-pointer text-lg"
            aria-label="Close message"
          >
            &times;
          </button>
          <h3 id="listing-message-title" className="font-serif text-xl mb-1 pr-10">
            Message {activeListing.company.name}
          </h3>
          <p className="text-[13px] text-text-muted mb-5">
            Re: {activeListing.title}
          </p>
          <label htmlFor="listing-message" className="sr-only">
            Message
          </label>
          <textarea
            ref={messageInputRef}
            id="listing-message"
            value={messageContent}
            onChange={(event) => setMessageContent(event.target.value)}
            rows={4}
            placeholder="Introduce yourself and explain why you're interested..."
            className="w-full px-4 py-3 border-[1.5px] border-border rounded-[10px] text-sm font-sans text-charcoal bg-warm-bg resize-y outline-none focus:border-gold focus:bg-white placeholder:text-[#B0A99F] transition-all mb-4"
            maxLength={1000}
          />
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void handleSendMessage()}
              disabled={actionLoading || !messageContent.trim()}
              className="min-h-[44px] px-6 py-3 rounded-sm text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading ? "Sending..." : "Send Message"}
            </button>
            <button
              type="button"
              onClick={() => setShowMessageModal(false)}
              className="min-h-[44px] px-6 py-3 rounded-sm text-[14px] font-semibold text-text-muted bg-transparent border-[1.5px] border-border cursor-pointer font-sans transition-all hover:border-navy/30"
            >
              Cancel
            </button>
          </div>
        </AccessibleModal>
      )}

      {interestError && (
        <div
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[300] bg-white rounded-md shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-red-200 px-5 py-3 animate-fade-in max-w-[340px] text-center"
          role="status"
        >
          <p className="text-sm text-red-600">
            {interestError}
          </p>
        </div>
      )}

      {showNoAvailability && (
        <AccessibleModal
          labelledBy="no-availability-title"
          onClose={() => setShowNoAvailability(false)}
          initialFocusRef={noAvailabilityCloseRef}
          returnFocusRef={returnFocusElementRef}
          maxWidthClass="max-w-[440px]"
        >
          <button
            ref={noAvailabilityCloseRef}
            type="button"
            onClick={() => setShowNoAvailability(false)}
            className="absolute top-4 right-4 min-w-[44px] min-h-[44px] rounded-full bg-warm-bg flex items-center justify-center text-text-muted hover:text-charcoal transition-colors border-none cursor-pointer text-lg"
            aria-label="Close availability prompt"
          >
            &times;
          </button>
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-gold mb-3"
            aria-hidden="true"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <h3 id="no-availability-title" className="font-serif text-xl mb-2 pr-10">
            Set up your work profile
          </h3>
          <p className="text-[14px] text-text-muted leading-relaxed mb-5">
            Add the essential details and seasonal availability employers need
            before you can express interest or start a conversation.
          </p>
          <Link
            href="/profile?setup=work#availability"
            className="inline-flex min-h-[44px] items-center px-6 py-3 rounded-sm text-[14px] font-semibold bg-gold text-white no-underline font-sans transition-all hover:bg-gold-light focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
          >
            Set up for work
          </Link>
        </AccessibleModal>
      )}
    </>
  );
}

function JobsWindowPickerModal({
  windows,
  action,
  onSelect,
  onClose,
  returnFocusRef,
}: {
  windows: CandidateWindow[];
  action: ListingAction;
  onSelect: (windowId: string) => void;
  onClose: () => void;
  returnFocusRef: RefObject<HTMLElement | null>;
}) {
  const firstWindowRef = useRef<HTMLButtonElement | null>(null);

  return (
    <AccessibleModal
      labelledBy="window-picker-title"
      onClose={onClose}
      initialFocusRef={firstWindowRef}
      returnFocusRef={returnFocusRef}
      maxWidthClass="max-w-[480px]"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 min-w-[44px] min-h-[44px] rounded-full bg-warm-bg flex items-center justify-center text-text-muted hover:text-charcoal transition-colors border-none cursor-pointer text-lg"
        aria-label="Close season picker"
      >
        &times;
      </button>
      <h3 id="window-picker-title" className="font-serif text-xl mb-1 pr-10">
        Which season?
      </h3>
      <p className="text-[13px] text-text-muted mb-5">
        {action === "interest"
          ? "Choose which availability window to register interest with"
          : "Choose which season to message about"}
      </p>
      <div className="flex flex-col gap-2">
        {windows.map((window, index) => {
          const roleLabels = window.positions
            .map((position) => findLabelById(ROLES_TREE, position))
            .filter(Boolean)
            .slice(0, 3);
          return (
            <button
              ref={index === 0 ? firstWindowRef : undefined}
              key={window.id}
              type="button"
              onClick={() => onSelect(window.id)}
              className="min-h-[44px] w-full px-4 py-3 rounded-[10px] text-left font-sans border-[1.5px] transition-all bg-warm-bg border-border-light cursor-pointer hover:border-gold hover:bg-gold/[0.03]"
            >
              <span className="text-[14px] font-semibold text-charcoal block mb-1">
                {window.season_label}
              </span>
              <span className="text-[12px] text-text-muted block mb-1.5">
                {new Date(window.available_from).toLocaleDateString("en-GB", {
                  month: "short",
                  year: "numeric",
                })}{" "}
                &ndash;{" "}
                {new Date(window.available_to).toLocaleDateString("en-GB", {
                  month: "short",
                  year: "numeric",
                })}
              </span>
              {roleLabels.length > 0 && (
                <span className="flex gap-1 flex-wrap">
                  {roleLabels.map((role) => (
                    <span
                      key={role}
                      className="bg-gold/[0.08] text-gold px-2 py-0.5 rounded-[5px] text-[10px] font-medium"
                    >
                      {role}
                    </span>
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </AccessibleModal>
  );
}
